// src/modules/socialhive/mobile/MobilePaymentPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./MobilePaymentPage.css";
import Services from "../../services/Services";
import { useLanguage } from "../../LanguageProvider";

/** ISO2 ⇒ 🇹🇷 flag emoji helper (UI için) */
function iso2ToFlag(iso2) {
    if (!iso2) return "";
    return iso2
        .toUpperCase()
        .replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt()));
}

/** Basit, arama destekli, klavye ile gezilebilir select */
function Combo({
    value,
    onChange,
    placeholder = "Seçiniz",
    options = [],
    getOptionLabel = (o) => o?.label ?? "",
    getOptionValue = (o) => o?.value ?? "",
    renderRight = null, // ör: seçilenin yanında fiyat/countryCode göster
    leftAdornment = null, // ör: flag
    searchKeys = ["label", "value"],
}) {
    const { getTranslation } = useLanguage();

    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const btnRef = useRef(null);
    const listRef = useRef(null);

    const selected = useMemo(
        () => options.find((o) => getOptionValue(o) === value) || null,
        [value, options]
    );

    const filtered = useMemo(() => {
        if (!query.trim()) return options;
        const q = query.toLowerCase();
        return options.filter((o) =>
            searchKeys.some((k) => String(o[k] || "").toLowerCase().includes(q))
        );
    }, [query, options]);

    useEffect(() => {
        function onDoc(e) {
            if (
                !btnRef.current?.contains(e.target) &&
                !listRef.current?.contains(e.target)
            ) {
                setOpen(false);
            }
        }
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    return (
        <div className="mp-select">
            <button
                type="button"
                ref={btnRef}
                className={`mp-select-control ${open ? "is-open" : ""}`}
                onClick={() => setOpen((s) => !s)}
                aria-expanded={open}
            >
                {/* {leftAdornment} */}
                <span className={`mp-select-value ${selected ? "has-value" : ""}`}>
                    {selected ? getOptionLabel(selected) : placeholder}
                </span>
                <span className="mp-select-caret">▾</span>
                {/* {renderRight && (
                    <span className="mp-select-right">
                        {renderRight(selected)}
                    </span>
                )} */}
            </button>

            {open && (
                <div className="mp-select-dropdown" ref={listRef} role="listbox">
                    <div className="mp-select-search">
                        <input
                            autoFocus
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={getTranslation("search")}
                        />
                    </div>
                    <div className="mp-select-list">
                        {filtered.length === 0 && (
                            <div className="mp-select-empty">...</div>
                        )}
                        {filtered.map((o) => {
                            const v = getOptionValue(o);
                            const selected = v === value;
                            return (
                                <button
                                    type="button"
                                    key={v}
                                    className={`mp-select-item ${selected ? "is-selected" : ""}`}
                                    onClick={() => {
                                        onChange?.(v);
                                        setOpen(false);
                                        setQuery("");
                                    }}
                                    role="option"
                                    aria-selected={selected}
                                >
                                    {o.flag && <span className="mp-select-flag">{o.flag}</span>}
                                    <span className="mp-select-label">{getOptionLabel(o)}</span>
                                    {o.right && (
                                        <span className="mp-select-right">{o.right}</span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

/** Tam ülke listesi (İngilizce adlar) */
const DEFAULT_COUNTRIES = [
    "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan",
    "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi",
    "Cabo Verde", "Cambodia", "Cameroon", "Canada", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo (Congo-Brazzaville)", "Costa Rica", "Côte d’Ivoire", "Croatia", "Cuba", "Cyprus", "Czechia",
    "Democratic Republic of the Congo", "Denmark", "Djibouti", "Dominica", "Dominican Republic",
    "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia",
    "Fiji", "Finland", "France",
    "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana",
    "Haiti", "Honduras", "Hungary",
    "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy",
    "Jamaica", "Japan", "Jordan",
    "Kazakhstan", "Kenya", "Kiribati", "Kuwait", "Kyrgyzstan",
    "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg",
    "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar",
    "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea", "North Macedonia", "Norway",
    "Oman",
    "Pakistan", "Palau", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal",
    "Qatar",
    "Romania", "Russia", "Rwanda",
    "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Korea", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria",
    "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu",
    "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States", "Uruguay", "Uzbekistan",
    "Vanuatu", "Venezuela", "Vietnam",
    "Yemen",
    "Zambia", "Zimbabwe"
].map((label) => ({ value: label, label }));

/** Tam telefon ülke kodları (ISO2 + dial). En yaygın/özel vakalar dahil. */
const DEFAULT_PHONE_CC = [
    { code: "TR", dial: "90", label: "Türkiye (+90)" },
    { code: "US", dial: "1", label: "United States (+1)" },
    { code: "CA", dial: "1", label: "Canada (+1)" },
    { code: "GB", dial: "44", label: "United Kingdom (+44)" },
    { code: "DE", dial: "49", label: "Germany (+49)" },
    { code: "FR", dial: "33", label: "France (+33)" },
    { code: "IT", dial: "39", label: "Italy (+39)" },
    { code: "ES", dial: "34", label: "Spain (+34)" },
    { code: "PT", dial: "351", label: "Portugal (+351)" },
    { code: "NL", dial: "31", label: "Netherlands (+31)" },
    { code: "BE", dial: "32", label: "Belgium (+32)" },
    { code: "SE", dial: "46", label: "Sweden (+46)" },
    { code: "NO", dial: "47", label: "Norway (+47)" },
    { code: "DK", dial: "45", label: "Denmark (+45)" },
    { code: "FI", dial: "358", label: "Finland (+358)" },
    { code: "RU", dial: "7", label: "Russia (+7)" },
    { code: "KZ", dial: "7", label: "Kazakhstan (+7)" },
    { code: "UA", dial: "380", label: "Ukraine (+380)" },
    { code: "BY", dial: "375", label: "Belarus (+375)" },
    { code: "PL", dial: "48", label: "Poland (+48)" },
    { code: "RO", dial: "40", label: "Romania (+40)" },
    { code: "HU", dial: "36", label: "Hungary (+36)" },
    { code: "CZ", dial: "420", label: "Czechia (+420)" },
    { code: "SK", dial: "421", label: "Slovakia (+421)" },
    { code: "CH", dial: "41", label: "Switzerland (+41)" },
    { code: "AT", dial: "43", label: "Austria (+43)" },
    { code: "IE", dial: "353", label: "Ireland (+353)" },
    { code: "IS", dial: "354", label: "Iceland (+354)" },
    { code: "GR", dial: "30", label: "Greece (+30)" },
    { code: "BG", dial: "359", label: "Bulgaria (+359)" },
    { code: "RS", dial: "381", label: "Serbia (+381)" },
    { code: "BA", dial: "387", label: "Bosnia & Herzegovina (+387)" },
    { code: "MK", dial: "389", label: "North Macedonia (+389)" },
    { code: "AL", dial: "355", label: "Albania (+355)" },
    { code: "MT", dial: "356", label: "Malta (+356)" },
    { code: "CY", dial: "357", label: "Cyprus (+357)" },
    { code: "EE", dial: "372", label: "Estonia (+372)" },
    { code: "LV", dial: "371", label: "Latvia (+371)" },
    { code: "LT", dial: "370", label: "Lithuania (+370)" },
    { code: "AZ", dial: "994", label: "Azerbaijan (+994)" },
    { code: "GE", dial: "995", label: "Georgia (+995)" },
    { code: "AM", dial: "374", label: "Armenia (+374)" },
    { code: "MD", dial: "373", label: "Moldova (+373)" },
    { code: "TR", dial: "90", label: "Türkiye (+90)" },
    { code: "IL", dial: "972", label: "Israel (+972)" },
    { code: "JO", dial: "962", label: "Jordan (+962)" },
    { code: "LB", dial: "961", label: "Lebanon (+961)" },
    { code: "IQ", dial: "964", label: "Iraq (+964)" },
    { code: "IR", dial: "98", label: "Iran (+98)" },
    { code: "SA", dial: "966", label: "Saudi Arabia (+966)" },
    { code: "AE", dial: "971", label: "United Arab Emirates (+971)" },
    { code: "QA", dial: "974", label: "Qatar (+974)" },
    { code: "KW", dial: "965", label: "Kuwait (+965)" },
    { code: "BH", dial: "973", label: "Bahrain (+973)" },
    { code: "OM", dial: "968", label: "Oman (+968)" },
    { code: "YE", dial: "967", label: "Yemen (+967)" },
    { code: "EG", dial: "20", label: "Egypt (+20)" },
    { code: "MA", dial: "212", label: "Morocco (+212)" },
    { code: "DZ", dial: "213", label: "Algeria (+213)" },
    { code: "TN", dial: "216", label: "Tunisia (+216)" },
    { code: "LY", dial: "218", label: "Libya (+218)" },
    { code: "ZA", dial: "27", label: "South Africa (+27)" },
    { code: "NG", dial: "234", label: "Nigeria (+234)" },
    { code: "GH", dial: "233", label: "Ghana (+233)" },
    { code: "KE", dial: "254", label: "Kenya (+254)" },
    { code: "TZ", dial: "255", label: "Tanzania (+255)" },
    { code: "UG", dial: "256", label: "Uganda (+256)" },
    { code: "CM", dial: "237", label: "Cameroon (+237)" },
    { code: "SN", dial: "221", label: "Senegal (+221)" },
    { code: "ET", dial: "251", label: "Ethiopia (+251)" },
    { code: "AO", dial: "244", label: "Angola (+244)" },
    { code: "MZ", dial: "258", label: "Mozambique (+258)" },
    { code: "IN", dial: "91", label: "India (+91)" },
    { code: "PK", dial: "92", label: "Pakistan (+92)" },
    { code: "BD", dial: "880", label: "Bangladesh (+880)" },
    { code: "LK", dial: "94", label: "Sri Lanka (+94)" },
    { code: "NP", dial: "977", label: "Nepal (+977)" },
    { code: "TH", dial: "66", label: "Thailand (+66)" },
    { code: "VN", dial: "84", label: "Vietnam (+84)" },
    { code: "MY", dial: "60", label: "Malaysia (+60)" },
    { code: "SG", dial: "65", label: "Singapore (+65)" },
    { code: "ID", dial: "62", label: "Indonesia (+62)" },
    { code: "PH", dial: "63", label: "Philippines (+63)" },
    { code: "CN", dial: "86", label: "China (+86)" },
    { code: "TW", dial: "886", label: "Taiwan (+886)" },
    { code: "HK", dial: "852", label: "Hong Kong (+852)" },
    { code: "JP", dial: "81", label: "Japan (+81)" },
    { code: "KR", dial: "82", label: "South Korea (+82)" },
    { code: "AU", dial: "61", label: "Australia (+61)" },
    { code: "NZ", dial: "64", label: "New Zealand (+64)" },
    { code: "BR", dial: "55", label: "Brazil (+55)" },
    { code: "AR", dial: "54", label: "Argentina (+54)" },
    { code: "CL", dial: "56", label: "Chile (+56)" },
    { code: "CO", dial: "57", label: "Colombia (+57)" },
    { code: "PE", dial: "51", label: "Peru (+51)" },
    { code: "VE", dial: "58", label: "Venezuela (+58)" },
    { code: "MX", dial: "52", label: "Mexico (+52)" }
].map((o) => ({ ...o, flag: iso2ToFlag(o.code) }));

export default function MobilePaymentPage({
    event,
    onBack,
    paymentData,
    getSessionToken,
    getCurrentUserId,
    onSuccess,
    countries,          // optional override (SHPaymentCard ile paylaşmak için)
    phoneCountryCodes,  // optional override (SHPaymentCard ile paylaşmak için)
}) {

    const { getTranslation } = useLanguage();

    const COUNTRIES = useMemo(
        () => (countries && countries.length ? countries : DEFAULT_COUNTRIES),
        [countries]
    );

    const PHONE_CC = useMemo(
        () =>
        (phoneCountryCodes && phoneCountryCodes.length
            ? phoneCountryCodes.map((o) => ({ ...o, flag: o.flag || iso2ToFlag(o.code) }))
            : DEFAULT_PHONE_CC),
        [phoneCountryCodes]
    );

    const [form, setForm] = useState({
        // Kart
        cardName: "",
        cardNumber: "",
        expiry: "",
        cvv: "",
        // İletişim
        phoneCountry: "TR",
        phoneLocal: "",
        email: "",
        // Kimlik & Fatura
        tckn: "", // zorunlu DEĞİL
        address: "",
        city: "",
        country: "Turkey", // İngilizce ülke adları ile SHPaymentCard country = "Turkey" uyumlu
        postalCode: "",
        taxCode: "",
    });

    const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    const dialFor = (iso2) =>
        PHONE_CC.find((c) => c.code === iso2)?.dial || "90";

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [htmlContent, setHtmlContent] = useState("");
    const [show3DS, setShow3DS] = useState(false);
    const [paymentID, setPaymentID] = useState("");
    const intervalRef = useRef(null);

    const totalPrice = paymentData?.subTotal || 0;
    const currency = "TL"; // SHPaymentCard sabit “TL”

    // Sayfanın en üst seviyede (component) içinde:
    useEffect(() => {
        const prev = document.body.style.overflow;
        // açılışta olası kilidi kaldır
        document.body.style.overflow = '';
        return () => {
            // bu sayfa çıkarken önceki değeri geri ver
            document.body.style.overflow = prev;
        };
    }, []);

    useEffect(() => {
        const isiOSDevice = (() => {
            const ua = navigator.userAgent || navigator.vendor || window.opera || "";
            const platform = navigator.platform || "";
            const iDevice = /iPad|iPhone|iPod/.test(ua);
            const iPadOS13plus = /MacIntel/.test(platform) && (navigator.maxTouchPoints || 0) > 1;
            return iDevice || iPadOS13plus;
        })();

        if (!isiOSDevice) return;
        document.documentElement.classList.add('is-ios');

        const setVars = () => {
            const vv = window.visualViewport;
            const vvh = Math.round(vv ? vv.height : window.innerHeight);
            const kb = vv ? Math.max(0, window.innerHeight - vvh) : 0;
            document.documentElement.style.setProperty('--vvh', `${vvh}px`);
            document.documentElement.style.setProperty('--keyboard-offset', `${kb}px`);
        };

        setVars();
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', setVars);
            window.visualViewport.addEventListener('scroll', setVars);
        }

        const onFocus = (e) => {
            const el = e.target;
            if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.getAttribute('contenteditable') === 'true')) {
                setTimeout(() => el.scrollIntoView({ block: 'center', behavior: 'smooth' }), 50);
            }
        };
        document.addEventListener('focusin', onFocus, { passive: true });

        return () => {
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', setVars);
                window.visualViewport.removeEventListener('scroll', setVars);
            }
            document.removeEventListener('focusin', onFocus);
        };
    }, []);

    // --- format helpers (SHPaymentCard ile uyumlu)
    const formatCardNumber = (value) => {
        const digits = value.replace(/\D/g, "").slice(0, 19);
        return digits.replace(/(\d{4})(?=\d)/g, "$1 ").trim();
    };
    const formatCVV = (value) => value.replace(/\D/g, "").slice(0, 4);
    const formatExpiry = (value) => {
        const digits = value.replace(/\D/g, "").slice(0, 4); // MMYY
        if (digits.length <= 2) return digits;
        return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    };

    // MM/YY → { mm, yy2, yy4 }
    const expiryParts = useMemo(() => {
        const raw = (form.expiry || "").replace(/\s/g, "");
        const [mmRaw, yyRaw] = raw.split("/");
        const mm = String(mmRaw || "").slice(0, 2).padStart(2, "0");
        let yy2 = String(yyRaw || "").slice(0, 2);
        const yy4 = yy2.length === 2 ? `20${yy2}` : yy2; // 07/30 → 2030
        return { mm, yy2, yy4 };
    }, [form.expiry]);

    const splitFullName = (full) => {
        const s = String(full || "").trim();
        if (!s) return { firstName: "", lastName: "" };
        const parts = s.split(/\s+/);
        if (parts.length === 1) return { firstName: parts[0], lastName: "" };
        return { firstName: parts.slice(0, -1).join(" "), lastName: parts.slice(-1)[0] };
    };

    const flattenPaidItems = () =>
        Object.values(paymentData?.ticketInfosMap || {})
            .flat()
            .filter(({ ticketPrice }) => Number(ticketPrice) > 0);

    const flattenAllItems = () =>
        Object.values(paymentData?.ticketInfosMap || {}).flat();

    const formattedItems = useMemo(
        () =>
            flattenPaidItems().map(({ ticketTitle, ticketType, ticketPrice }) => ({
                title: ticketTitle,
                type: "VIRTUAL",
                price: String(ticketPrice),
            })),
        [paymentData]
    );

    const fullGsmNumber = useMemo(() => {
        const dial = dialFor(form.phoneCountry);
        const local = (form.phoneLocal || "").replace(/\D/g, "");
        return `+${dial}${local}`;
    }, [form.phoneCountry, form.phoneLocal]);

    // TCKN zorunlu değil
    const isFormValid = () => {
        return (
            form.cardName.trim() &&
            form.cardNumber.replace(/\s/g, "").length >= 12 &&
            expiryParts.mm &&
            expiryParts.yy4 &&
            form.cvv.length >= 3 &&
            form.phoneLocal.trim() &&
            form.email.trim() &&
            form.address.trim() &&
            form.city.trim() &&
            form.country.trim() &&
            form.postalCode.trim()
        );
    };

    const handleComplete = async () => {
        if (isSubmitting) return;
        const token = getSessionToken?.();
        if (!token) {
            return;
        }

        // Ücretsiz güvenlik kalkanı
        if (totalPrice <= 0) {
            try {
                const billing = {
                    tckn: form.tckn,
                    address: form.address,
                    city: form.city,
                    region: form.country,
                    postalCode: form.postalCode,
                    taxCode: form.taxCode,
                    country: form.country,
                    phone: fullGsmNumber,
                    email: form.email,
                };
                const all = flattenAllItems();
                for (const t of all) {
                    await Services.SocialHiveCreateSoldTicket(
                        token,
                        t.channelID,
                        t.eventID,
                        t.ticketID,
                        "free",
                        t.name,
                        t.lastname,
                        t.email,
                        t.phone,
                        true,
                        billing
                    );
                }
                onSuccess?.({ total: totalPrice, count: all.length, currency });
            } catch (e) {
                console.error("Free-inside-payment failed", e);
            }
            return;
        }

        if (!isFormValid()) return;

        try {
            setIsSubmitting(true);
            const { firstName, lastName } = splitFullName(form.cardName);
            const userId = getCurrentUserId?.();
            const identityNo = form.tckn?.trim() || "11111111111";

            const initRes = await Services.InitializeSHPayment(
                userId,
                form.cardName,
                form.cardNumber.replace(/\s/g, ""),
                expiryParts.mm,  // "07"
                expiryParts.yy4, // "2030"
                form.cvv,
                firstName,
                lastName,
                fullGsmNumber,
                form.email,
                form.address,
                form.city,
                form.country,     // ÜLKE ADI
                form.postalCode,
                String(totalPrice),
                "TL",
                identityNo,
                formattedItems
            );

            if (initRes?.data?.status && initRes?.data?.htmlContent) {
                setHtmlContent(initRes.data.htmlContent);
                setPaymentID(initRes.data.paymentID);
                setShow3DS(true);
            } else {
                setIsSubmitting(false);
            }
        } catch (e) {
            console.error("InitializeSHPayment error", e);
            setIsSubmitting(false);
        }
    };

    // 3DS sonucu dinleme ve başarılıysa bilet oluşturma
    useEffect(() => {
        if (!show3DS || !paymentID) return;
        intervalRef.current = setInterval(async () => {
            try {
                const r = await Services.ListenSHPayment(paymentID);
                const status = r?.data?.status;
                if (status === "1" || status === "2" || (status && status !== "5")) {
                    clearInterval(intervalRef.current);
                    setShow3DS(false);
                    setHtmlContent("");
                    if (status === "1") {
                        try {
                            const token = getSessionToken?.();
                            const billing = {
                                tckn: form.tckn,
                                address: form.address,
                                city: form.city,
                                region: form.country,
                                postalCode: form.postalCode,
                                taxCode: form.taxCode,
                                country: form.country,
                                phone: fullGsmNumber,
                                email: form.email,
                            };
                            const all = flattenAllItems();
                            for (const t of all) {
                                await Services.SocialHiveCreateSoldTicket(
                                    token,
                                    t.channelID,
                                    t.eventID,
                                    t.ticketID,
                                    paymentID,
                                    t.name,
                                    t.lastname,
                                    t.email,
                                    t.phone,
                                    true,
                                    billing
                                );
                            }
                            setIsSubmitting(false);
                            onSuccess?.({ total: totalPrice, count: all.length, currency });
                        } catch (e) {
                            console.error("CreateSoldTicket after payment error", e);
                            setIsSubmitting(false);
                            onSuccess?.({ total: totalPrice, count: flattenAllItems().length, currency });
                        }
                    } else {
                        setIsSubmitting(false);
                    }
                }
            } catch (e) {
                console.error("ListenSHPayment error", e);
            }
        }, 1000);

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [show3DS, paymentID]); // eslint-disable-line react-hooks/exhaustive-deps

    // Seçenek objeleri (Combo için zengin render)
    const phoneOptions = useMemo(
        () =>
            PHONE_CC.map((c) => ({
                ...c,
                value: c.code,
                right: `+${c.dial}`,
            })),
        [PHONE_CC]
    );

    const countryOptions = COUNTRIES.map((c) => ({
        ...c,
        value: c.value,
    }));

    return (
        <div className="mp-page">
            <header className="mp-top">
                <button className="mp-back" onClick={onBack}>‹</button>
                <h1 className="mp-title">{event?.title}</h1>
            </header>

            <div className="mp-cover">
                <img src={event?.cover} alt={event?.title} />
            </div>

            <div className="mp-body" style={{ marginBottom: "85px" }}>

                <div className="mp-form">
                    <label>{getTranslation("nameOnCard")}*</label>
                    <input
                        value={form.cardName}
                        onChange={(e) => setField("cardName", e.target.value)}
                        placeholder={getTranslation("name") + " " + getTranslation("lastname")}
                        autoComplete="cc-name"
                    />

                    <label>{getTranslation("cardNumber")}*</label>
                    <input
                        value={form.cardNumber}
                        onChange={(e) => setField("cardNumber", formatCardNumber(e.target.value))}
                        placeholder="4444 4444 4444 4444"
                        inputMode="numeric"
                        autoComplete="cc-number"
                    />
                    <div className="mp-row">
                        <div style={{ gap: "10px", display: "flex", flexDirection: "column" }}>
                            <div style={{ gap: "10px", display: "flex", flexDirection: "column" }}>
                                <label>{getTranslation("expiry")}*</label>
                                <input
                                    value={form.expiry}
                                    onChange={(e) => setField("expiry", formatExpiry(e.target.value))}
                                    placeholder="07/30"
                                    inputMode="numeric"
                                    autoComplete="cc-exp"
                                />
                            </div>
                        </div>
                        <div style={{ gap: "10px", display: "flex", flexDirection: "column" }}>
                            <label>CVV*</label>
                            <input
                                value={form.cvv}
                                onChange={(e) => setField("cvv", formatCVV(e.target.value))}
                                placeholder="123"
                                inputMode="numeric"
                                autoComplete="cc-csc"
                            />
                        </div>
                    </div>

                    <div className="mp-row">

                        <div style={{ gap: "10px", display: "flex", flexDirection: "column" }}>
                            <label>{getTranslation("countryCode")}*</label>
                            <div className="mp-phone-combo">
                                <Combo
                                    value={form.phoneCountry}
                                    onChange={(v) => setField("phoneCountry", v)}
                                    options={phoneOptions}
                                    placeholder="Ülke kodu"
                                    getOptionLabel={(o) => `${o.flag} ${o.label}`}
                                    getOptionValue={(o) => o.value}
                                    renderRight={(sel) => (sel ? `+${sel.dial}` : "")}
                                    leftAdornment={
                                        <span className="mp-flag">
                                            {iso2ToFlag(form.phoneCountry || "TR")}
                                        </span>
                                    }
                                    searchKeys={["label", "code", "dial"]}
                                />
                            </div>
                        </div>

                        <div style={{ gap: "10px", display: "flex", flexDirection: "column" }}>
                            <label>{getTranslation("phone")}*</label>
                            <input
                                className="mp-phone-local"
                                value={form.phoneLocal}
                                onChange={(e) =>
                                    setField("phoneLocal", e.target.value.replace(/[^\d\s]/g, ""))
                                }
                                placeholder="5xx xxx xx xx"
                                inputMode="tel"
                                autoComplete="tel"
                            />
                        </div>
                    </div>

                    <label>{getTranslation("email")}*</label>
                    <input
                        value={form.email}
                        onChange={(e) => setField("email", e.target.value)}
                        placeholder="mail@example.com"
                        inputMode="email"
                        autoComplete="email"
                    />

                    {/* TCKN zorunlu değil */}
                    <label>T.C.K.N</label>
                    <input
                        value={form.tckn}
                        onChange={(e) => setField("tckn", e.target.value.replace(/\D/g, "").slice(0, 11))}
                        placeholder="T.C.K.N"
                        inputMode="numeric"
                    />

                    <label>{getTranslation("address")}*</label>
                    <input
                        value={form.address}
                        onChange={(e) => setField("address", e.target.value)}
                        placeholder={getTranslation("address")}
                        rows={3}
                        style={{ resize: "vertical" }}
                    />

                    <div className="mp-row">
                        <div style={{ gap: "10px", display: "flex", flexDirection: "column" }}>
                            <label>{getTranslation("city")}*</label>
                            <input
                                value={form.city}
                                onChange={(e) => setField("city", e.target.value)}
                                placeholder={getTranslation("city")}
                                autoComplete="address-level2"
                            />
                        </div>
                        <div style={{ gap: "10px", display: "flex", flexDirection: "column" }}>
                            <label>{getTranslation("country")}*</label>
                            <Combo
                                value={form.country}
                                onChange={(v) => setField("country", v)}
                                options={countryOptions}
                                placeholder="Ülke seçiniz"
                                getOptionLabel={(o) => o.label}
                                getOptionValue={(o) => o.value}
                                leftAdornment={<span className="mp-flag">{iso2ToFlag(form.phoneCountry)}</span>}
                                searchKeys={["label", "value"]}
                            />
                        </div>
                    </div>

                    <div className="mp-row" style={{ marginBottom: '335px' }}>
                        <div style={{ gap: "10px", display: "flex", flexDirection: "column" }}>
                            <label>{getTranslation("postalCode")}*</label>
                            <input
                                value={form.postalCode}
                                onChange={(e) => setField("postalCode", e.target.value.replace(/\D/g, "").slice(0, 10))}
                                placeholder="27000"
                                inputMode="numeric"
                                autoComplete="postal-code"
                            />
                        </div>
                        <div style={{ gap: "10px", display: "flex", flexDirection: "column" }}>
                            <label>{getTranslation("taxNoOptional")}</label>
                            <input
                                value={form.taxCode}
                                onChange={(e) => setField("taxCode", e.target.value)}
                                placeholder={getTranslation("taxNoOptional")}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="mp-footer">
                <div className="mp-footer-left">
                    <div className="mp-footer-label">{getTranslation("myTickets")}:</div>
                    <div className="mp-footer-price">
                        {totalPrice} {currency}
                    </div>
                </div>
                <button
                    className={`mp-btn-primary ${isSubmitting ? 'loading' : ''}`}
                    onClick={handleComplete}
                    disabled={isSubmitting || (totalPrice > 0 && !isFormValid())}
                >
                    {getTranslation("completeTransaction")}
                </button>
            </div>

            {show3DS && htmlContent && (
                <iframe
                    srcDoc={htmlContent}
                    title="3D Secure"
                    style={{
                        width: "100vw",
                        height: "100vh",
                        border: "none",
                        position: "fixed",
                        top: 0,
                        left: 0,
                        zIndex: 9999,
                        backgroundColor: "#fff",
                    }}
                />
            )}
        </div>
    );
}